import { isDestroyed, isDestroying } from '@ember/destroyable';
import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { NewIssue } from 'lumen/controllers/market/parity/parity-manager/addissue';
import { formatUrl } from 'lumen/utils/rates-request';
import { timeout } from 'lumen/utils/waiters';

type HotelInMarketSubscription = NonNullable<NewIssue['subscription']>;

export interface HotelInMarketOption {
  label: string;
  subscription: HotelInMarketSubscription;
}

function subscriptionToOption(
  subscription: HotelInMarketSubscription,
): HotelInMarketOption {
  return {
    label: `${subscription.name} ${subscription.brand_code}`,
    subscription,
  };
}

interface ParityParityManagerIssueDetailHotelInMarketSearchSignature {
  Args: {
    selectItem: (option: HotelInMarketOption) => void;
    selectedOption: NewIssue['subscription'];
  };
}

export default class ParityParityManagerIssueDetailHotelInMarketSearch extends Component<ParityParityManagerIssueDetailHotelInMarketSearchSignature> {
  @service declare ajax: Services['ajax'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare pmRequestService: Services['pm-request-service'];

  @tracked isOpen = false;
  @tracked options: HotelInMarketOption[] = [];
  @tracked optionsFilter = '';
  @tracked page = 0;
  declare isLoading: boolean;

  fetchOptionsTask = task({ restartable: true }, async () => {
    let options: HotelInMarketOption[] = [];

    try {
      const path = '/api/v3/autocomplete/hotelsinmarket/';
      const params = {
        market_id: this.hotelAndMarketService.market?.id,
        q: this.optionsFilter,
      };

      const urlAutocomplete = formatUrl(path, params, this.ajax.host);

      const hotelsInMarket = (await this.pmRequestService.requestData(
        urlAutocomplete,
      )) as { results: HotelInMarketSubscription[] };
      options = [
        ...options,
        ...hotelsInMarket.results.map(subscriptionToOption),
      ];
    } catch (error) {
      this.pmRequestService.handleRequestError(error);
    } finally {
      this.isLoading = false;
    }

    return options;
  });

  searchOptionTask = task(
    { restartable: true },
    async (optionsFilter: string) => {
      await timeout();

      this.optionsFilter = optionsFilter;
      this.page = 0;
      this.options = await this.fetchOptionsTask.perform();
    },
  );

  get value(): HotelInMarketOption | null {
    const subscription = this.args.selectedOption;

    if (!subscription) {
      return null;
    }

    return subscriptionToOption(subscription);
  }

  resetValues(): void {
    this.optionsFilter = '';
    this.page = 0;
    this.options = [];
  }

  @action
  selectOption(option: HotelInMarketOption): void {
    this.optionsFilter = '';
    this.args.selectItem(option);
  }

  @action
  toggleDropdown(): void {
    if (isDestroyed(this) || isDestroying(this)) {
      return;
    }

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      void this.searchOptionTask.perform(this.optionsFilter);
    } else {
      this.resetValues();
    }
  }

  <template>
    <div class="flex flex-col">
      <PrismComboSelectInput
        @isSearchable={{true}}
        @isSecondary={{true}}
        @loadingMessage={{concat (t "general.loading") "..."}}
        @onChange={{this.selectOption}}
        @onClose={{this.toggleDropdown}}
        @onOpen={{this.toggleDropdown}}
        @onSearch={{this.searchOptionTask.perform}}
        @options={{this.options}}
        @placeholder={{t "rates.hotelname"}}
        @searchMessage={{t "general.autocomplete.type_to_search"}}
        @unsafe_matchTriggerWidth={{true}}
        @value={{this.value}}
      />
    </div>
  </template>
}
